import { useEffect, useMemo, useRef } from 'react';
import { ChatMessage } from '@/components/chat-message';
import { EmptyState } from '@/components/empty-state';
import { Loader2, MessageSquare } from 'lucide-react';
import type { Message } from '@/types';

interface ChatPanelProps {
  messages: Message[];
  loading: boolean;
  onConfirmGdd?: (msgId: string) => void;
  onChoice?: (field: string, value: string, msgId?: string) => void;
  /** 重复指令二次确认：用户选择复用/重新生成 */
  onDuplicateChoice?: (choice: 'reuse' | 'regenerate') => void;
  /** 视频转试玩确认：用户点击确认生成 */
  onVideoConfirm?: (messageId: string) => void;
  /** 视频素材确认卡片：用户确认视频顺序与图片槽位 */
  onVideoMaterialConfirm?: (messageId: string, videoOrder: string[], imageRoles: Array<{ fileName: string; role: string }>, videoOrderIndexes?: number[]) => void;
  /** 视频方案确认卡片（FEAT-20260907-VIDEO-PLAN）：用户确认方案或倒计时自动触发 */
  onVideoPlanConfirm?: (messageId: string) => void;
  /** 错误消息重试：点击后重新发送之前的用户消息 */
  onRetry?: (messageId: string) => void;
  executionState?: { title: string; detail?: string };
  /** BUG-20260908-PW-001: 会话管线状态（video 方案卡倒计时防重启自动重发用） */
  videoPipelineStatus?: 'running' | 'done' | 'paused' | null;
}

export function ChatPanel({
  messages,
  loading,
  onConfirmGdd,
  onChoice,
  onDuplicateChoice,
  onVideoConfirm,
  onVideoMaterialConfirm,
  onVideoPlanConfirm,
  onRetry,
  executionState,
  videoPipelineStatus,
}: ChatPanelProps): JSX.Element {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, executionState?.title, executionState?.detail]);

  // FR-11：最新一条 GDD 卡片可交互，更早的 GDD 卡片置灰降级
  const lastGddMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' && (
        /<GDD>[\s\S]*?<\/GDD>/i.test(m.content ?? '')
        || (m.metadata?.gddSpec && typeof m.metadata.gddSpec === 'object')
        || m.metadata?.gddEvent
      )) return m.id;
    }
    return undefined;
  }, [messages]);

  if (messages.length === 0 && !loading && !executionState) {
    return (
      <EmptyState
        icon={<MessageSquare size={48} />}
        title="开始对话"
        description="描述你想要的游戏玩法，Agent 将协助你完成创作"
      />
    );
  }

  return (
    // 卡片贴边布局（用户指定）：完全贴最左/最右无内缩，每条消息自身最宽 80%（见 ChatMessage）
    <div className="flex-1 overflow-y-auto space-y-4">
      {messages.map((msg) => (
        <ChatMessage
          key={msg.id}
          message={msg}
          isLatestGdd={msg.id === lastGddMessageId}
          videoPipelineStatus={videoPipelineStatus}
          onConfirmGdd={onConfirmGdd}
          onChoice={onChoice}
          onDuplicateChoice={onDuplicateChoice}
          onVideoConfirm={onVideoConfirm}
          onVideoMaterialConfirm={onVideoMaterialConfirm}
          onVideoPlanConfirm={onVideoPlanConfirm}
          onRetry={onRetry}
        />
      ))}
      {executionState && (
        <div className="relative animate-slide-up pl-4">
          <span aria-hidden className="absolute inset-y-1 left-0 w-[2px] rounded-full bg-neon-cyan" />
          <div className="flex items-center gap-3 rounded-lg border border-neon-cyan/25 bg-neon-cyan/5 px-3 py-2.5">
            <Loader2 size={16} className="shrink-0 animate-spin text-neon-cyan" />
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-foreground">{executionState.title}</p>
              {executionState.detail && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{executionState.detail}</p>}
            </div>
          </div>
        </div>
      )}
      {loading && (
        <div className="flex items-center gap-2 py-2">
          <span className="typing-dot h-2 w-2 rounded-full bg-neon-cyan" />
          <span className="typing-dot h-2 w-2 rounded-full bg-neon-cyan" />
          <span className="typing-dot h-2 w-2 rounded-full bg-neon-cyan" />
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
